import * as yup from 'yup';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { getAsqRespondentChoices, getAsqSurveyList, postAsqSurvey } from 'helpers/backend-helper';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  DailyActivityResponseSectionPostData,
  OverallEvaluationResponsePostData,
  RespondentChoice,
  RespondentFormData,
  RespondentPostData,
  Survey,
  SurveyItem
} from '../../../types/asq';
import { toast } from 'react-toastify';
import { SelectAsqSurveyField } from '../../../components/Asq/FormField/SelectAsqSurveyField';
import { QuestionContainer } from '../../../components/Asq/Containers/QuestionContainer';
import { FlexColumnItemContainer } from '../../../components/Asq/Containers/FlexColumnItemContainer';
import Title from '../../../components/Title';
import { AsqHeader, AsqFooter } from '../../../components/Asq/AsqBranding';
import { Respondent } from '../../../components/Asq/Containers/Respondent';
import { DailyActivityContainer } from '../../../components/Asq/Containers/DailyActivity';
import { OverallEvaluationContainer } from '../../../components/Asq/Containers/OverallEvaluation';
import Button from '../../../components/Button';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';

const DEFAULT_DAILY_ACTIVITY_RESPONSE_GROUP_VALUES: {
  sections: DailyActivityResponseSectionPostData[];
} = {
  sections: []
};

const DEFAULT_OVERALL_EVALUATION_RESPONSE_GROUP_VALUES: {
  responses: OverallEvaluationResponsePostData[];
} = {
  responses: []
};

const DEFAULT_RESPONDENT_VALUES: RespondentFormData = {
  last_name_of_child: '',
  first_name_of_child: '',
  birth_date_of_child: {
    startDate: undefined,
    endDate: undefined
  },
  gestational_age_of_child: undefined,
  sex_of_child: '',
  filler_last_name: '',
  filler_first_name: '',
  filler_relationship_to_child: '',
  filler_relationship_to_child_detail: '',
  filler_postal_code: '',
  filler_address: '',
  filler_phone_number: '',
  filler_email: '',
  assisted_by: '',
  age_in_months: undefined,
  age_in_days: undefined,
  corrected_age_in_months: undefined,
  corrected_age_in_days: undefined,
  daily_activity_response_group: DEFAULT_DAILY_ACTIVITY_RESPONSE_GROUP_VALUES,
  overall_evaluation_response_group: DEFAULT_OVERALL_EVALUATION_RESPONSE_GROUP_VALUES
};

const birthDateOfChildSchema = yup.object().shape({
  startDate: yup
    .date()
    .test('startDate', '生年月日を入力してください', (value) => value !== undefined && value !== null)
    .max(new Date(), '未来の日付は入力できません'),
  endDate: yup
    .date()
    .test('endDate', '生年月日を入力してください', (value) => value !== undefined && value !== null)
    .max(new Date(), '未来の日付は入力できません')
    .min(yup.ref('startDate'), '開始日より前の日付は入力できません')
});

const dailyActivityResponseSchema = yup.object().shape({
  question: yup.number().required(),
  choice: yup.number().typeError('選択してください').required('選択してください'),
  additional_answer: yup.string()
});

const dailyActivityResponseSectionSchema = yup.object().shape({
  section: yup.number().required(),
  responses: yup.array().of(dailyActivityResponseSchema).required()
});

const dailyActivityResponseGroupSchema = yup.object().shape({
  sections: yup.array().of(dailyActivityResponseSectionSchema).required()
});

const overallEvaluationResponseSchema = yup.object().shape({
  question: yup.number().required(),
  choice: yup.number(),
  // choice が choice_for_detail で指定された「はい」か「いいえ」の場合、
  // detail は必須だがそのバリデーションをここで書くのは難しいので、onError でバリデーションする
  detail: yup.string()
});

const overallEvaluationResponseGroupSchema = yup.object().shape({
  responses: yup.array().of(overallEvaluationResponseSchema).required()
});
const validationSchema = yup.object().shape({
  last_name_of_child: yup.string(),
  first_name_of_child: yup.string(),
  birth_date_of_child: birthDateOfChildSchema.required('生年月日を入力してください'),
  gestational_age_of_child: yup
    .number()
    .positive('在胎週数は正の数を入力してください')
    .integer('在胎週数は整数を入力してください'), // 在胎週数
  sex_of_child: yup.string().required('性別を選択してください'),
  filler_last_name: yup.string(),
  filler_first_name: yup.string(),
  filler_relationship_to_child: yup.string().required('お子さんとの関係を選択してください'),
  // filler_relationship_to_child_detail はもし filler_relationship_to_child が「その他」の場合に必須
  filler_relationship_to_child_detail: yup.string().when('filler_relationship_to_child', {
    is: 'other',
    then: (schema) => schema.required('お子さんとの関係を入力してください'),
    otherwise: (schema) => schema
  }),
  // 郵便番号
  filler_postal_code: yup.string().test('postalCode', '郵便番号は7桁の数字で入力してください', (value) => {
    if (!value) return true;
    return /^\d{7}$/.test(value);
  }),
  filler_address: yup.string(),
  filler_phone_number: yup.string().test('phoneNumber', '電話番号の10か11桁の数字で入力してください', (value) => {
    if (!value) return true;
    return /^0\d{9,10}$/.test(value);
  }),
  filler_email: yup
    .string()
    .email('メールアドレスの形式が正しくありません')
    .required('メールアドレスを入力してください'),
  assisted_by: yup.string(),
  age_in_months: yup
    .number()
    .test('required', '月齢を入力してください', (value) => {
      return value !== undefined;
    })
    .min(0, '月齢は0以上の整数を入力してください')
    .integer('月齢は整数を入力してください'),
  age_in_days: yup
    .number()
    .test('required', '日齢を入力してください', (value) => {
      return value !== undefined;
    })
    .min(0, '日齢は0以上の整数を入力してください')
    .integer('日齢は整数を入力してください'),
  corrected_age_in_months: yup
    .number()
    .min(0, '月齢は0以上の整数を入力してください')
    .integer('月齢は整数を入力してください'),
  corrected_age_in_days: yup
    .number()
    .min(0, '日齢は0以上の整数を入力してください')
    .integer('日齢は整数を入力してください'),
  daily_activity_response_group: dailyActivityResponseGroupSchema.required(),
  overall_evaluation_response_group: overallEvaluationResponseGroupSchema.required()
});

const title = 'Ages & Stages Questionnaires (ASQ)';

export const AsqSurveyPage = () => {
  const [surveyList, setSurveyList] = useState<SurveyItem[]>();
  const [surveyDetail, setSurveyDetail] = useState<Survey>();
  const [respondentChoice, setRespondentChoice] = useState<RespondentChoice>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  const {
    setValue,
    setError,
    handleSubmit,
    watch,
    reset,
    formState: { errors }
  } = useForm<RespondentFormData>({
    resolver: yupResolver(validationSchema),
    defaultValues: DEFAULT_RESPONDENT_VALUES
  });

  const currentFormData = watch();

  /**
   * サーバーサイドから提供される質問データ surveyDetail に基づいて、回答データの初期値を生成する。
   * surveyDetail が変更されたときのみ再計算する。
   */
  const respondentValues = useMemo(() => {
    if (!surveyDetail) return DEFAULT_RESPONDENT_VALUES;
    return {
      ...DEFAULT_RESPONDENT_VALUES,
      daily_activity_response_group: {
        sections: surveyDetail.daily_activity.sections.map((section) => ({
          section: section.id,
          responses: section.questions.map((question) => ({
            question: question.id,
            // 初期状態では未選択。送信前に必須バリデーションで弾く。
            // 型は number 必須だが未選択UI保持のため一旦 undefined を許容し、実入力時に number が入る。
            choice: undefined as unknown as number,
            additional_answer: ''
          }))
        }))
      },
      overall_evaluation_response_group: {
        responses: surveyDetail.overall_evaluation.questions.map((question) => ({
          question: question.id,
          choice: undefined,
          detail: ''
        }))
      }
    } as RespondentFormData;
  }, [surveyDetail]);

  const fetchSurveyList = async () => {
    try {
      const sl = await getAsqSurveyList();
      setSurveyList(sl);
    } catch (e: any) {
      toast.error('ASQのデータ取得中にエラーが発生しました');
    }
  };
  const fetchRespondentChoices = async () => {
    try {
      const rc = await getAsqRespondentChoices();
      setRespondentChoice(rc);
    } catch (e: any) {
      toast.error('ASQ回答者選択肢のデータ取得中にエラーが発生しました');
    }
  };

  /**
   * ページ表示時にASQのデータを取得する
   */
  useEffect(() => {
    fetchSurveyList();
    fetchRespondentChoices();
  }, []);

  /**
   * ASQのデータが取得できたら、フォームフィールドをデフォルトからリセットする
   */
  useEffect(() => {
    reset(respondentValues);
  }, [reset, respondentValues]);

  // 生年月日（必須）と在胎週数（任意）から、月齢/日齢・修正月齢/日齢を自動計算してセット
  const calcMonthsAndDays = useCallback((birth: Date, base: Date) => {
    const birthDay = dayjs(birth);
    const baseDay = dayjs(base);
    // 暦の月差（切り捨て）
    let months = baseDay.diff(birthDay, 'month');
    // 残り日数は、birth + monthsヶ月 からの差
    let anchor = birthDay.add(months, 'month');
    // anchorがbaseを超えてしまうケースの補正
    if (anchor.isAfter(baseDay)) {
      months -= 1;
      anchor = birthDay.add(months, 'month');
    }
    let days = baseDay.diff(anchor, 'day');
    if (days < 0) {
      // 念のためのガード（通常ここには来ない）
      months -= 1;
      days = baseDay.diff(birthDay.add(months, 'month'), 'day');
    }
    return { months, days };
  }, []);

  const calcCorrectedMonthsAndDays = useCallback(
    (birth: Date, base: Date, gestationalAgeWeeks?: number | null) => {
      const actual = calcMonthsAndDays(birth, base);

      if (gestationalAgeWeeks === undefined || gestationalAgeWeeks === null) {
        return actual;
      }

      // 37週未満のみ補正し、24か月未満の範囲に限る
      if (gestationalAgeWeeks >= 37 || actual.months >= 24) {
        return actual;
      }

      const weeksPremature = 40 - gestationalAgeWeeks; // 40週を正期産とする
      const correctedBase = dayjs(base).subtract(weeksPremature, 'week').toDate();
      return calcMonthsAndDays(birth, correctedBase);
    },
    [calcMonthsAndDays]
  );

  useEffect(() => {
    const birth = currentFormData.birth_date_of_child?.startDate as unknown as Date | undefined;
    if (!birth) return;
    const base = new Date(); // 評価日は「本日」

    const { months, days } = calcMonthsAndDays(birth, base);
    setValue('age_in_months', Math.max(0, months));
    setValue('age_in_days', Math.max(0, days));

    const { months: cMonths, days: cDays } = calcCorrectedMonthsAndDays(
      birth,
      base,
      currentFormData.gestational_age_of_child ?? undefined
    );
    setValue('corrected_age_in_months', Math.max(0, cMonths));
    setValue('corrected_age_in_days', Math.max(0, cDays));
  }, [
    currentFormData.birth_date_of_child?.startDate,
    currentFormData.gestational_age_of_child,
    setValue,
    calcMonthsAndDays,
    calcCorrectedMonthsAndDays
  ]);

  const onSubmit = async (data: RespondentFormData) => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    // YYYY-MM-DD の形式に変換
    const postData: RespondentPostData = {
      ...data,
      birth_date_of_child: dayjs(currentFormData.birth_date_of_child.startDate).format('YYYY-MM-DD')
    };

    try {
      const res: any = await postAsqSurvey(postData);
      // sessionStorage に保存（タイトル含む）。必要なら savedAt で期限管理
      if (res && res.result && Array.isArray(res.result.sections)) {
        const compact = {
          title: surveyDetail?.title, // 結果ページでサブ表示するために調査名を保存
          sections: res.result.sections.map((s: any) => ({
            id: s.id,
            title: s.title,
            total_score: s.total_score,
            cutoff_gray: s.cutoff_gray,
            cutoff_black: s.cutoff_black
          })),
          savedAt: Date.now()
        };
        try {
          sessionStorage.setItem('asq_result', JSON.stringify(compact));
        } catch (err) {
          console.warn('Failed to write asq_result to sessionStorage', err);
        }
      }
      navigate('/asq/result');
    } catch (e: any) {
      // TODO バックエンドのエラーハンドリングを実装する
      console.error(e);
    }
    console.log('onSubmit end');
    setIsSubmitting(false);
  };

  const onError = () => {
    if (!surveyDetail) return;

    const overallEvaluationResponses = currentFormData.overall_evaluation_response_group.responses;
    const overallEvaluationQuestions = surveyDetail.overall_evaluation.questions;

    // overall_evaluation の choice が choice_for_detail で指定された「はい」か「いいえ」の場合、detail は必須
    overallEvaluationResponses.forEach((response, index) => {
      if (response.choice === overallEvaluationQuestions[index].choice_for_detail && !response.detail) {
        setError(`overall_evaluation_response_group.responses.${index}.detail`, {
          type: 'required',
          message: '詳細を入力してください'
        });
      }
    });

    console.log(errors);
  };

  // 質問データをフェッチしてformにセットして質問群を表示できるかのフラグ
  const isReady = useMemo(() => {
    return (
      surveyDetail!! &&
      currentFormData.overall_evaluation_response_group.responses.length > 0 &&
      currentFormData.daily_activity_response_group.sections.length > 0
    );
  }, [
    currentFormData.daily_activity_response_group.sections.length,
    currentFormData.overall_evaluation_response_group.responses.length,
    surveyDetail
  ]);

  return (
    <div className='min-h-screen flex flex-col bg-gray-50'>
      <AsqHeader />

      <main className='container mx-auto py-10 flex flex-col items-center gap-6 sm:gap-10 flex-1 w-full'>
        <div className='w-[80%] md:w-full'>
          <Title title={title} />
        </div>
        <div className='md:px-10 px-4 w-full mx-auto'>
          <div className='flex flex-col gap-6 sm:gap-10'>
            {/*select ASQ*/}
            <FlexColumnItemContainer>
              <QuestionContainer questionText={'対象月齢を選択してください'} required={false}>
                <SelectAsqSurveyField
                  surveyList={surveyList}
                  setSurveyDetail={setSurveyDetail}
                  surveyDetail={surveyDetail}
                />
              </QuestionContainer>
            </FlexColumnItemContainer>

            {/*show selected ASQ detail*/}
            {isReady && (
              <>
                <Respondent
                  currentFormValues={currentFormData}
                  setValue={setValue}
                  errors={errors}
                  respondentChoice={respondentChoice}
                />
                <DailyActivityContainer
                  key={'daily_activity'}
                  dailyActivity={surveyDetail!.daily_activity}
                  setValue={setValue}
                  currentFormData={currentFormData}
                  formErrors={errors}
                />
                <OverallEvaluationContainer
                  key={'overall_evaluation'}
                  overallEvaluation={surveyDetail!.overall_evaluation}
                  setValue={setValue}
                  currentFormData={currentFormData}
                  formErrors={errors}
                />
              </>
            )}
            {
              /* errors オブジェクト に中身があったら表示 */
              Object.keys(errors).length > 0 && (
                <div className='mt-2 text-sm text-red'>入力エラーがあります。必須項目を入力してください。</div>
              )
            }
            {surveyDetail && (
              <div className='w-full flex justify-center'>
                <Button title='送信' onClick={handleSubmit(onSubmit, onError)} />
              </div>
            )}
          </div>
        </div>
      </main>

      <AsqFooter />
    </div>
  );
};
